// ============================================
// Checkout Service
// Calls startCheckout Cloud Function (PHI-enforced).
//
// HIPAA: Only fulfillment-safe supplement fields are sent.
// Clinical fields (tierRecommendation, summary, warnings,
// safetyWarnings, rationale, targetBiomarker, evidenceLevel)
// are NEVER included in the checkout payload.
//
// requiresProvider is derived server-side from supplement.requiresRx.
// The clinical tierRecommendation field is NOT read by this function.
// ============================================

import type { Protocol, ShippingAddress } from '../../types';

import { CF_URLS } from '../../config/api';

// Use the PHI-enforced startCheckout endpoint
const CHECKOUT_URL = CF_URLS.startCheckout;

interface CheckoutResponse {
  sessionUrl: string;
  orderId: string;
}

export async function startCheckout(
  protocol: Protocol,
  customerEmail: string,
  shippingAddress?: ShippingAddress,
): Promise<CheckoutResponse> {
  // HIPAA: Send ONLY allow-listed fulfillment fields.
  // The server (startCheckout CF) enforces the allow-list and validates schema.
  // requiresProvider is intentionally NOT sent — the CF derives it from
  // supplement.requiresRx flags, keeping clinical judgment server-side.
  const response = await fetch(CHECKOUT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      protocolSnapshot: {
        supplements: protocol.supplements.map((s) => ({
          name: s.name,
          dosage: s.dosage,
          timing: s.timing,
          // R10: productId required for Cloud Function SKU mapping
          productId: s.productId ?? '',
          requiresRx: s.requiresRx,
        })),
        bundleName: protocol.bundleName,
        bundlePrice: protocol.bundlePrice,
        bundleId: protocol.bundleId,
        // NOTE: tierRecommendation, summary, warnings, safetyWarnings
        // are intentionally excluded — clinical fields never enter the checkout path.
      },
      customerEmail,
      ...(shippingAddress ? { shippingAddress } : {}),
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to create checkout session');
  }

  return response.json();
}
